import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, MoonIcon, SunIcon } from 'lucide-react';
import AlgaResponseWidget from './widgets/AlgaResponseWidget';
interface AlgaAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  searchBarRect: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}
const AlgaAssistant: React.FC<AlgaAssistantProps> = ({
  isOpen,
  onClose,
  searchBarRect
}) => {
  const [query, setQuery] = useState('');
  const [conversation, setConversation] = useState<Array<{
    type: 'user' | 'assistant';
    content: string;
  }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showWidgetResponse, setShowWidgetResponse] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);
  // Handle body class when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('alga-open');
    } else {
      document.body.classList.remove('alga-open');
    }
    return () => {
      document.body.classList.remove('alga-open');
    };
  }, [isOpen]);
  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setQuery('');
        setConversation([]);
        setShowWidgetResponse(false);
      }, 500); // Wait for close animation to finish
    }
  }, [isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    // Add user message to conversation
    setConversation([...conversation, {
      type: 'user',
      content: query
    }]);
    // Simulate typing
    setIsTyping(true);
    // Simulate assistant response after a delay
    setTimeout(() => {
      setIsTyping(false);
      setConversation(prev => [...prev, {
        type: 'assistant',
        content: 'Ecco alcune informazioni che potrebbero esserti utili:'
      }]);
      setShowWidgetResponse(true);
    }, 1500);
    setQuery('');
  };
  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  if (!isOpen) return null;
  return <>
      {/* Overlay for background dimming effect */}
      <div className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-300" style={{
      opacity: isOpen ? 1 : 0,
      backdropFilter: 'blur(2px)'
    }} onClick={onClose} data-id="element-32" />
      {/* Modal container */}
      <div ref={modalRef} className={`fixed inset-0 z-50 flex flex-col overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'} transition-all duration-300 ease-out`} style={{
      opacity: isOpen ? 1 : 0,
      transform: isOpen ? 'scale(1)' : 'scale(0.98)'
    }} data-id="element-33">
        <div className="flex justify-between items-center p-4 px-6 md:px-8" data-id="element-34">
          <div className="flex flex-col items-start" data-id="element-35">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kYK3VqJh5ErppvW42Sa6wk/IMG_1178.png" alt="EIIS Logo" className="h-12 object-contain" data-id="element-36" />
            <p className="text-xs text-[#919191] mt-1" data-id="element-37">
              Powered by EIIS Intelligence
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-38">
            <button onClick={toggleDarkMode} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-[#212121]' : 'hover:bg-gray-100'} transition-colors`} data-id="element-39">
              {isDarkMode ? <SunIcon size={24} className="text-gray-300" data-id="element-40" /> : <MoonIcon size={24} className="text-gray-600" data-id="element-41" />}
            </button>
            <button onClick={onClose} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`} data-id="element-42">
              <XIcon size={24} data-id="element-43" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 md:px-8 pb-6" data-id="element-44">
          <div className="max-w-3xl mx-auto" data-id="element-45">
            {conversation.length === 0 && <div className="flex flex-col items-center justify-center h-full" data-id="element-46">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-pink-500 mb-6" data-id="element-47">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-48" />
                </div>
                <h2 className={`text-2xl font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`} data-id="element-49">
                  Ciao, sono ALGA
                </h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-center max-w-lg mb-8`} data-id="element-50">
                  Il tuo assistente AI personale. Posso aiutarti con
                  informazioni sui corsi, suggerimenti di studio, e molto altro.
                </p>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`} data-id="element-51">
                  Prova a chiedermi qualcosa...
                </p>
              </div>}
            {conversation.length > 0 && <div className="space-y-6 pt-6" data-id="element-52">
                {conversation.map((message, index) => <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-53">
                    {message.type === 'assistant' && <div className="w-10 h-10 rounded-full overflow-hidden border-3 border-pink-500 mr-3 flex-shrink-0" data-id="element-54">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-55" />
                      </div>}
                    <div className={`max-w-[80%] p-3 rounded-lg ${message.type === 'user' ? 'bg-custom-green text-white rounded-tr-none' : isDarkMode ? 'bg-[#171717] text-gray-100 rounded-tl-none border border-[#171717]' : 'bg-gray-100 text-gray-800 rounded-tl-none'}`} data-id="element-56">
                      {message.content}
                    </div>
                  </div>)}
                {isTyping && <div className="flex justify-start" data-id="element-57">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-3 border-pink-500 mr-3 flex-shrink-0" data-id="element-58">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-59" />
                    </div>
                    <div className={`${isDarkMode ? 'bg-[#171717] text-gray-100 border border-[#171717]' : 'bg-gray-100 text-gray-800'} p-4 rounded-lg rounded-tl-none`} data-id="element-60">
                      <div className="flex space-x-1" data-id="element-61">
                        <div className={`w-2 h-2 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'} rounded-full animate-bounce`} data-id="element-62"></div>
                        <div className={`w-2 h-2 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'} rounded-full animate-bounce`} style={{
                    animationDelay: '0.2s'
                  }} data-id="element-63"></div>
                        <div className={`w-2 h-2 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'} rounded-full animate-bounce`} style={{
                    animationDelay: '0.4s'
                  }} data-id="element-64"></div>
                      </div>
                    </div>
                  </div>}
                {showWidgetResponse && <AlgaResponseWidget isDarkMode={isDarkMode} data-id="element-65" />}
              </div>}
          </div>
        </div>
        <div className={`border-t ${isDarkMode ? 'border-[#171717] bg-black' : 'border-gray-200 bg-white'}`} data-id="element-66">
          <div className="max-w-3xl mx-auto px-4 py-4" data-id="element-67">
            <form onSubmit={handleSubmit} className="flex items-center" data-id="element-68">
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Scrivi un messaggio..." className={`flex-1 p-3 rounded-l-full focus:outline-none focus:ring-2 focus:ring-custom-green focus:border-transparent ${isDarkMode ? 'bg-[#171717] border border-[#171717] text-gray-100 placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-800 placeholder-gray-500'}`} data-id="element-69" />
              <button type="submit" className="bg-custom-green text-white p-3 rounded-r-full hover:bg-custom-green/90 focus:outline-none focus:ring-2 focus:ring-custom-green" data-id="element-70">
                <SendIcon size={20} data-id="element-71" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </>;
};
export default AlgaAssistant;